<script setup>
import { computed, ref } from 'vue'
import Card from 'primevue/card';
import SpeedDial from 'primevue/speeddial';
import { useEmployeeStore } from "@/stores/employee.js";
import Divider from "primevue/divider";
import Avatar from "primevue/avatar";
import EmployeeForm from './EmployeeForm.vue';
import ConfirmDeleteDialog from '@/components/common/ConfirmDeleteDialog.vue';
import ConfirmArchiveDialog from '@/components/common/ConfirmArchiveDialog.vue';
import PermissionsMatrixDialog from '@/components/rbac/PermissionsMatrixDialog.vue';
import {useRoute, useRouter} from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { usePermissions } from '@/composables/usePermissions.js';



const props = defineProps({
  employee: {
    type: Object,
    default: null
  }
});

const route = useRoute()
const router = useRouter()
const employeeStore = useEmployeeStore();
const { hasPermission } = usePermissions();
const showEditDialog = ref(false);
const showConfirmDelete = ref(false);
const showConfirmArchive = ref(false);
const showConfirmUnarchive = ref(false);
const showPermissionsDialog = ref(false);
const toast = useToast();


const apiOrigin = computed(() => {
  try {
    const u = new URL(import.meta.env.VITE_API_BASE_URL, window.location.origin);
    return u.origin;
  } catch {
    return window.location.origin;
  }
});

const fileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${apiOrigin.value}${path.startsWith('/') ? '' : '/'}${path}`;
};

const fullName = computed(() => {
  if (!props.employee) return 'ФИО сотрудника';
  const lastName = props.employee.last_name || '';
  const firstName = props.employee.first_name || '';
  return [lastName, firstName].filter(Boolean).join(' ');
});

const initials = computed(() => {
  const firstName = props.employee?.first_name || '';
  const lastName = props.employee?.last_name || '';
  return ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() || '?';
});

const phone = computed(() => props.employee?.work_phone || props.employee?.mobile_phone || '—');

const isArchived = computed(() => props.employee?.is_active === false);

async function handleSave({ id, data }) {
  try {
    await employeeStore.updateEmployee(id, data);
    toast.add({
      severity: 'success',
      summary: 'Сотрудник обновлен',
      detail: 'Данные сотрудника успешно сохранены',
      life: 2500
    });
    showEditDialog.value = false;
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось сохранить изменения сотрудника',
      life: 4000
    });
  }
}


async function handleConfirmDelete() {
  if (!props.employee?.id) return;
  try {
    await employeeStore.deactivateEmployee(props.employee.id);
    toast.add({
      severity: 'success',
      summary: 'Сотрудник деактивирован',
      detail: 'Пользователь перемещён в архив',
      life: 2500
    });
    await router.push('/employees');
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось деактивировать сотрудника',
      life: 4000
    });
  } finally {
    showConfirmDelete.value = false;
  }
}

async function onArchiveConfirm() {
  if (!props.employee?.id) return;
  try {
    await employeeStore.archiveEmployee(props.employee.id);
    toast.add({
      severity: 'success',
      summary: 'Сотрудник архивирован',
      detail: 'Сотрудник перемещён в архив',
      life: 2500
    });
    await router.push('/employees');
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось архивировать сотрудника',
      life: 4000
    });
  }
}

async function onUnarchiveConfirm() {
  if (!props.employee?.id) return;
  try {
    await employeeStore.unarchiveEmployee(props.employee.id);
    toast.add({
      severity: 'success',
      summary: 'Сотрудник возвращён из архива',
      detail: 'Статус сотрудника изменён на ACTIVE',
      life: 2500
    });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось вернуть сотрудника из архива',
      life: 4000
    });
  }
}

function handlePermissionsSaved() {
  toast.add({
    severity: 'success',
    summary: 'Права сохранены',
    detail: 'Права доступа сотрудника успешно обновлены',
    life: 2500
  });
  showPermissionsDialog.value = false;
}


const menuItems = computed(() => {
  const items = []

  // Редактировать - требует employee.change_employeeprofile
  if (hasPermission('employee.change_employeeprofile')) {
    items.push({
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: () => {
        showEditDialog.value = true;
      }
    })
  }

  // Права - требует common.manage_permissions (управление правами доступа)
  if (hasPermission('common.manage_permissions')) {
    items.push({
      label: 'Права',
      icon: 'pi pi-key',
      command: () => {
        showPermissionsDialog.value = true;
      }
    })
  }

  // Архивировать/Вернуть из архива - требует employee.archive_employeeprofile
  if (hasPermission('employee.archive_employeeprofile')) {
    items.push({
      label: isArchived.value ? 'Вернуть из архива' : 'Архивировать',
      icon: isArchived.value ? 'pi pi-sun' : 'pi pi-moon',
      severity: isArchived.value ? 'success' : 'danger',
      command: () => {
        const currentStatus = isArchived.value
        if (currentStatus) {
          showConfirmUnarchive.value = true
        } else {
          showConfirmArchive.value = true
        }
      }
    })
  }

  // Удалить - требует employee.delete_employeeprofile
  if (hasPermission('employee.delete_employeeprofile')) {
    items.push({
      label: 'Удалить',
      icon: 'pi pi-trash',
      severity: 'danger',
      command: () => {
        showConfirmDelete.value = true;
      }
    })
  }

  return items
});
</script>

<template>
  <div v-if="employee" class="flex flex-col items-center">
    <Card class="w-full max-w-md shadow p-0 overflow-hidden">
      <template #header>
        <div class="relative">
          <img alt="user header" src="@/assets/card_bgheader.png" class="w-full h-32 object-cover" />
          <div class="absolute top-2 right-2 z-10">
            <SpeedDial
                :model="menuItems"
                direction="down"
                :showIcon="'pi pi-ellipsis-v'"
                :hideIcon="'pi pi-times'"
                buttonClass="p-button-text p-button-secondary"
            />
          </div>
        </div>
      </template>

      <template #content>
        <div class="flex relative flex-col items-center py-5">
          <div class="absolute left-1/2 -translate-x-1/2 -top-8 z-10">
            <Avatar
                v-if="fileUrl(employee.avatar)"
                :image="fileUrl(employee.avatar)"
                size="xlarge"
                shape="circle"
                class="shadow-lg border-4 border-white"
            />
            <Avatar
                v-else
                :label="initials"
                size="xlarge"
                shape="circle"
                class="shadow-lg border-4 border-white"
                style="background-color: #9ca3af; color: white;"
            />
          </div>

          <div class="h-6"></div>

          <h2 class="text-lg font-semibold mb-2 text-center">
            {{ fullName }}
          </h2>

          <Divider class="w-2/3 my-3" />

          <div class="flex justify-between w-full text-sm gap-4 px-4">
            <div class="flex-1 flex flex-col space-y-4">
              <div>
                <span class="font-semibold">Email:</span><br>
                {{ employee.email || '—' }}
              </div>
              <div>
                <span class="font-semibold">Телефон:</span><br>
                {{ phone }}
              </div>
            </div>

            <div class="flex-1 flex flex-col space-y-4">
              <div>
                <span class="font-semibold">Должность:</span><br>
                {{ employee.post || '—' }}
              </div>
              <div>
                <span class="font-semibold">Отдел:</span><br>
                {{ employee.department || '—' }}
              </div>
            </div>
          </div>

          <Divider class="w-2/3 my-3" />

          <div class="flex flex-col mb-4 items-start w-full gap-4 px-4">
            <div class="text-sm text-left">
              <span class="font-semibold">Образование:</span><br>
              <p>{{ employee.qualification || '—' }}</p>
              <p>{{ employee.additional_education || '—' }}</p>
            </div>
          </div>
        </div>
      </template>
    </Card>

    <EmployeeForm
        v-if="employee"
        v-model:visible="showEditDialog"
        :employee="employee"
        @save="handleSave"
    />

    <ConfirmDeleteDialog
        v-model:visible="showConfirmDelete"
        :customerName="fullName"
        @confirm="handleConfirmDelete"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmArchive"
        :employeeName="fullName"
        @confirm="onArchiveConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmUnarchive"
        mode="unarchive"
        :employeeName="fullName"
        @confirm="onUnarchiveConfirm"
    />

    <PermissionsMatrixDialog
        v-model:visible="showPermissionsDialog"
        entity-type="user"
        :entity-id="employee?.id"
        :entity-name="fullName"
        @saved="handlePermissionsSaved"
    />

  </div>
  <div v-else class="text-center p-4">
    Загрузка данных сотрудника...
  </div>
</template>

<style scoped>
:deep(.p-card-body) {
  padding-top: 0;
  padding-bottom: 0;
}
</style>